/*
The MyApps.tsx file is a React component that displays a list of applications (apps) to the user. It uses TypeScript and is styled with styled-components.
The file starts by defining an interface MyAppsProps which describes the properties that the MyApps component expects. These properties include arrays of applications, organizations, user accounts, and errors, among other things.
The MyApps component uses useState to manage the state of the app registration process and useEffect to fetch the status of the registration when the component mounts or when certain props change.
The component also defines several helper functions. For example, getTiles is used to collect and mutate the appropriate tiles from the apps, getErrorMessage is used to generate an error message based on the errors and apps passed to it, and getWidgetHeight is used to calculate the height of the widget based on the number of apps.
The component's render method returns a JSX structure that includes a wrapper, a header, a select wrapper for selecting an organization, a button for requesting access, and a list of apps. The list of apps is either a RichAppList or a StandardAppListDummy, depending on whether the apps are loaded or not.
The file also includes several styled components, such as Wrapper, Header, SelectWrapper, ButtonWrapper, Apps, TextWrapper, and others. These are used to style the different parts of the component.
*/

import React, {
  useState,
  ReactElement,
  useEffect,
  useRef,
  useLayoutEffect,
  useCallback,
} from 'react';
import { toast } from 'react-toastify';
import api, { TilesResponse } from 'shared/api/myapps';
import Div from 'shared/components/Div';
import { Organization, User, UserAccount } from 'shared/state/misc/oidc';
import styled from 'styled-components';
import { AppRegistrationStatus } from 'types/types';
import { Config, EnhancedTile } from './types';
import { useTrackWidget } from 'shared/util/analytics/useTrackWidget';
import alphasort from 'shared/util/alphasort';
import Typography from 'shared/components/Typography';
import URI from 'urijs';
import { sanitizeUrl } from 'shared/widgets/RichTextEditor/utils/url';
import {
  SELF_REGISTRATION_SUPPORTED_TENANCIES,
  UserAccessRequest,
} from 'client/constants';
import AccessRequestDialogContainer from './AccessRequestDialog/AccessRequestDialogContainer';
import env from 'client/config';
import builderenv from 'builder/config';
import Message from 'shared/components/Message';
import Loading from 'shared/components/Loading';
import RichAppList from './components/RichAppList';
import StandardAppListDummy from './components/StandardAppList';
import { Select, SelectChangeEvent, MenuItem } from '@mui/material';
import { cardThemeOptions } from 'builder/util/constants';
import { defaultTheme } from 'shared/theme/theme';
import { Button } from 'shared/theme/link-ui-react/Button';
import { Link } from 'shared/theme/link-ui-react/Link';
import { calculateMyAppsHeight } from './MyApps.util';

const EXTRA_BOTTOM_GAP_PX = 12;

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 8px;
`;

const StyledDialog = styled.h3`
  display: flex;
  font-size: larger;
  color: ${defaultTheme.colors.aux.black};
  align-items: center;
  font-family: OptumSans, sans-serif;
`;

const Header = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-bottom: 16px;
`;

const SelectWrapper = styled(Div)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  & > div > div {
    font-family: OptumSans, sans-serif;
  }
`;

const CustomLink = styled(Link)`
  margin-right: 5px;
  font-size: larger;
  color: ${defaultTheme.colors.aux.darkBlue};
  & span {
    font-size: inherit;
  }
`;

const SelectLabel = styled(Typography)<{ cardTheme: string }>`
  color: ${p =>
    !p.cardTheme || p.cardTheme === cardThemeOptions.Light
      ? `${defaultTheme.colors.aux.darkGrey}`
      : '#ffffff'};
`;

const FlexSelect = styled(Select)<{
  width?: number;
  cardTheme: string;
}>`
  div {
    cursor: default;
  }
  width: ${p => p.width}px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Apps = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  min-height: 400px;
`;

const TextWrapper = styled.div`
  padding-bottom: 10px;
  font-family: OptumSans, sans-serif;
`;

// Session-scoped cache to preserve environment selection across remounts
// Keyed by widget id; resets naturally on page reload
const envSelectionCache: Map<string, string> = new Map();

const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const filterEnvironment = (
  enableAppEnvironment: boolean,
  envName: string,
  tile: Tile
) => {
  if (enableAppEnvironment) {
    return tile.envName
      ? tile.envName.toLowerCase() === envName.toLowerCase()
      : false;
  }
  return true;
};

const getTiles = (
  apps: Array<Application>,
  tileType: string,
  siteDomain: string,
  enableAppEnvironment: boolean,
  envName: string,
  orgId?: string
): Array<EnhancedTile> => {
  /**
   * Collect the appropriate tiles and mutate them:
   * - Add a linkOrgId query param if an orgId is present
   * - Add the app's theme color to eachf
   */
  return apps
    .reduce((acc, app) => {
      return acc.concat(
        app.tiles
          .filter(tile => tile.type === tileType)
          .filter(tile =>
            filterEnvironment(enableAppEnvironment, envName, tile)
          )
          .map(origTile => {
            const tile: EnhancedTile = {
              ...origTile,
              emulationEnabled: app.emulationEnabled,
              themeColor: app.themeColor ? `#${app.themeColor}` : '#d3d3d3',
            };
            if (tile.targetURL) {
              const tileUri = URI(tile.targetURL);
              if (tile.assistant) {
                tileUri.addQuery({ domain: siteDomain });
              }
              if (orgId) {
                tileUri.addQuery({ linkOrgId: orgId });
              }
              // Use href() for explicit string extraction to satisfy lint rule
              // Use href() for explicit string extraction to satisfy lint rule
              const updatedUrlString: string = tileUri.href();
              // Sanitize the URL before assigning
              tile.targetURL = sanitizeUrl(updatedUrlString);
            }
            return tile;
          })
      );
    }, [] as Array<EnhancedTile>)
    .sort((a, b) => alphasort(a.internalName, b.internalName));
};

const getErrorMessage = (errors: Array<string>, apps: Array<Application>) => {
  if (apps.length > 0) {
    if (
      errors.includes('OPServiceFailed') ||
      errors.includes('LSServiceFailed')
    ) {
      return 'There was a system error.  If you are not able to locate your app, try closing your browser and sign-in with a new session.';
    } else {
      return 'There was a system error due to an invalid user identifier. If you are not able to locate your app, please contact your administrator or support.';
    }
  } else if (errors.includes('Invalid User Identifier')) {
    return 'We are unable to locate your account. Please contact your administrator or support.';
  } else {
    return 'We are facing a technical issue. Please try again.';
  }
};

export interface MyAppsProps extends WidgetRenderProps<Config> {
  myapps: Array<Application>;
  myappsErrors: Array<string>;
  myappsMeta: StateMeta;
  // domain currently unused; reserved for future multi-domain analytics expansion
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for future multi-domain analytics expansion
  domain: string;
  published: boolean;
  orgs: Array<Organization>;
  userAccounts: Array<UserAccount>;
  activeOrgId: string;
  fetchMyApps: (published: boolean) => Promise<TilesResponse>;
  setActiveOrgId: (id: string) => void;
  // onClose not presently used by widget; keep for dialog compatibility
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for dialog compatibility
  onClose?: () => void;
  openAccessRequestDialog?: () => void;
  siteDomain: string;
  user: User;
  enableRegister: boolean;
  appRegistration?: AppRegistration;
  noAppsMessage?: string;
  tenancy?: string;
  siteProvisioningStores?: Array<number>;
  enableAppEnvironment?: boolean;
  appsEnvironmentList?: string;
  config?: Config;
  // uniqueId reserved for forthcoming personalization features
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for personalization features
  uniqueId?: string;

  onUpdateConfig?: (
    siteId: number,
    pageId: number,
    widgetId: string,
    config: any,
    defaultWidget: boolean
  ) => void;
}

export const MyApps: React.FC<MyAppsProps> = (
  props: MyAppsProps
): ReactElement => {
  // Extract optional props to incorporate into tracking (prevents unused prop lint warnings)
  const { domain, onClose: onCloseCallback, uniqueId } = props;
  const [registration, setRegistration] = useState({
    resume: false,
    isFetching: false,
    error: false,
    isSaving: false,
  });

  const {
    paddingData,
    borderData,
    lineColor,
    cardStyle,
    visible,
    isCustomWidget,
    font,
    cardTheme,
    analyticsLabel,
  } = props.widgetState.config;

  const enableAppEnvironmentResolved =
    props.enableAppEnvironment ??
    props.widgetState.config.enableAppEnvironment ??
    false;
  const appsEnvironmentListResolved =
    props.appsEnvironmentList ??
    props.widgetState.config.appsEnvironmentList ??
    '';
  const environment = appsEnvironmentListResolved
    ? capitalizeFirstLetter(appsEnvironmentListResolved.split(',')[0])
    : '';
  // Derive initial environment from the provided list (session-only; no persistence)
  const environmentListRaw = appsEnvironmentListResolved
    ? appsEnvironmentListResolved
        .split(',')
        .map(value => value.trim())
        .filter(value => value !== '')
        .map(str => str.charAt(0).toUpperCase() + str.slice(1))
    : [];
  const cachedEnv = envSelectionCache.get(props.widgetState.id) ?? '';
  const initialEnv = environmentListRaw.includes(cachedEnv)
    ? cachedEnv
    : environmentListRaw[0] ?? environment;
  const [appEnv, setAppEnv] = useState(initialEnv);
  const [isEnvOpen, setIsEnvOpen] = useState(false);

  useEffect(() => {
    if (appsEnvironmentListResolved) {
      const list = appsEnvironmentListResolved
        .split(',')
        .map(value => value.trim())
        .filter(value => value !== '')
        .map(str => str.charAt(0).toUpperCase() + str.slice(1));
      // Keep current selection if still valid; otherwise fall back to first
      if (!list.includes(appEnv)) {
        const next = list[0] ?? '';
        setAppEnv(next);
        envSelectionCache.set(props.widgetState.id, next);
      }
    }
  }, [appsEnvironmentListResolved]);

  useEffect(() => {
    if (props.myapps.length === 0 && props.enableRegister && props.published) {
      fetchStepupStatus();
    }
  }, [props.myapps, props.enableRegister, props.published]);

  const fetchStepupStatus = () => {
    setRegistration(prevState => ({ ...prevState, isFetching: true }));
    api
      .getStepupStatus(props.appRegistration.businessKey)
      .then(appReq => {
        if (appReq.status === AppRegistrationStatus.Pending) {
          setRegistration(prevState => ({
            ...prevState,
            resume: true,
            isFetching: false,
          }));
        } else {
          setRegistration(prevState => ({ ...prevState, isFetching: false }));
        }
      })
      .catch(e => {
        if (
          e.response.status === 400 &&
          e.response.data.message.includes('No Request found')
        ) {
          setRegistration(prevState => ({ ...prevState, isFetching: false }));
        } else {
          toast('Error fetching registration status', {
            type: 'error',
            theme: 'colored',
          });
          setRegistration(prevState => ({
            ...prevState,
            error: true,
            isFetching: false,
          }));
        }
      });
  };

  const handleRequestAccessClick = () => {
    const { openAccessRequestDialog } = props;
    openAccessRequestDialog();
  };

  // the below code repeatedly calling widgetWrapper which is causing issue
  const {
    fetchMyApps,
    published,
    enableRegister,
    // NOTE: refactor opportunity – successful does not appear in props until after a request is made; kept for future enhancement.
    myappsMeta: { pending, successful: appsRequestHappened = null },
  } = props;
  useEffect(() => {
    if (pending === false && appsRequestHappened === null) {
      fetchMyApps(published).then(response => {
        if (response.appTiles.length === 0) {
          // if no apps is provisioned to user and registration is enabled for the site
          // then get the registration status
          enableRegister && published && fetchStepupStatus();
        }
      });
    }
  }, [published, props]);

  const headerRef = useRef<HTMLDivElement>(null);
  const appsRef = useRef<HTMLDivElement>(null);
  const lastReportedHeightRef = useRef(props.widgetState.config.height);

  useEffect(() => {
    lastReportedHeightRef.current = props.widgetState.config.height;
  }, [props.widgetState.config.height]);

  /**
   * Measures the rendered MyApps content and reports its height to PageLayout.
   *
   * @returns Nothing. The current widget configuration is updated when its
   * measured height changes.
   */
  const measureAndUpdateHeight = useCallback((): void => {
    try {
      // Avoid interfering with select interactions.
      if (isEnvOpen) return;

      const headerEl = headerRef.current;
      const appsEl = appsRef.current;
      if (!appsEl) return;

      const headerMarginBottom = headerEl
        ? Number.parseFloat(
            globalThis.getComputedStyle(headerEl).marginBottom || '0'
          ) || 0
        : 0;
      const newHeight = calculateMyAppsHeight({
        appsHeight: appsEl.scrollHeight,
        headerHeight: headerEl?.offsetHeight ?? 0,
        headerMarginBottom,
        paddingTop: paddingData?.top ?? 0,
        paddingBottom: paddingData?.bottom ?? 0,
        extraBottomGap: EXTRA_BOTTOM_GAP_PX,
      });

      if (
        newHeight <= 0 ||
        newHeight === lastReportedHeightRef.current ||
        typeof props.onUpdateConfig !== 'function'
      ) {
        return;
      }

      const pageIdToUse =
        props.pages?.find(page => page.current?.slug === 'myapps')?.id ||
        props.currentPage?.pageId;
      lastReportedHeightRef.current = newHeight;
      props.onUpdateConfig(
        props.siteId,
        pageIdToUse,
        props.widgetState.id,
        {
          ...props.widgetState.config,
          height: newHeight,
        },
        false
      );
    } catch (err) {
      console.error('Error measuring widget height:', err);
    }
  }, [
    isEnvOpen,
    paddingData?.bottom,
    paddingData?.top,
    props.currentPage?.pageId,
    props.onUpdateConfig,
    props.pages,
    props.siteId,
    props.widgetState.config,
    props.widgetState.id,
  ]);

  /**
   * Observes responsive content changes and coalesces height measurements into
   * a single animation frame so card wrapping cannot leave PageLayout with a
   * stale widget height.
   */
  useLayoutEffect(() => {
    let animationFrameId: number | null = null;

    const scheduleMeasurement = () => {
      if (animationFrameId !== null) return;
      animationFrameId = globalThis.requestAnimationFrame(() => {
        animationFrameId = null;
        measureAndUpdateHeight();
      });
    };

    const resizeObserver = new ResizeObserver(scheduleMeasurement);
    const headerEl = headerRef.current;
    const appsEl = appsRef.current;

    if (headerEl) resizeObserver.observe(headerEl);
    if (appsEl) resizeObserver.observe(appsEl);

    // Preserve the delayed initial measurement so asynchronously rendered app
    // content has settled before its first height is reported.
    const initialMeasurementId = globalThis.setTimeout(
      scheduleMeasurement,
      150
    );

    return () => {
      globalThis.clearTimeout(initialMeasurementId);
      resizeObserver.disconnect();
      if (animationFrameId !== null) {
        globalThis.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [
    appEnv,
    enableAppEnvironmentResolved,
    isEnvOpen,
    measureAndUpdateHeight,
    props.activeOrgId,
    props.myapps,
    props.myappsMeta.pending,
    props.myappsMeta.successful,
  ]);

  const setOrgSelectWidth = (): number => {
    const { orgs } = props;
    const charSizes = orgs.map(org => org.name.length).sort((a, b) => a - b);
    const maxLength = Math.max(...charSizes);
    // Expanded nested ternary for clarity & lint compliance
    if (maxLength > 18 && maxLength < 24) {
      return 250;
    }
    if (maxLength > 24) {
      return 300;
    }
    return 200;
  };

  const handleOrgChange = (e: SelectChangeEvent<unknown>) => {
    const { activeOrgId, setActiveOrgId, fetchMyApps, published } = props;
    const nextOrgId = String(e.target.value);
    if (activeOrgId !== nextOrgId) {
      setActiveOrgId(nextOrgId);
      fetchMyApps(published);
    }
  };

  const handleEnvironmentChange = (e: SelectChangeEvent<unknown>) => {
    const next = String(e.target.value);
    setAppEnv(next);
    envSelectionCache.set(props.widgetState.id, next);
  };

  const handleRetry = () => {
    const { fetchMyApps, published, enableRegister, myapps } = props;
    fetchMyApps(published).then(() => {
      if (myapps.length === 0) {
        enableRegister && published && fetchStepupStatus();
      }
    });
  };
  const trackMyAppsWidget = useTrackWidget({
    tracking: (props as any).tracking,
    widgetState: props.widgetState,
    currentPage: props.currentPage,
    site: props.site,
  });

  const handleClick = (e: MouseEvent, tile: EnhancedTile) => {
    if (props.editing) return; // suppress in builder editing mode
    trackMyAppsWidget('myapps_widget_click', {
      tileId: tile.tileId,
      tileLabel: tile.label,
      tileInternalName: tile.internalName,
      tileType: tile.type,
      activeDate: tile.activeDate,
      targetUrl: tile.targetURL || null,
      themeColor: tile.themeColor,
      widgetAnalyticsLabel: analyticsLabel,
      clickPageX: (e as any).pageX,
      clickPageY: (e as any).pageY,
      domain: domain || null,
      uniqueId: uniqueId || null,
    });
  };
  const initiateRegistration = () => {
    const { appRegistration } = props;
    setRegistration(prev => ({ ...prev, isSaving: true }));
    api
      .postStepup({ businessKey: appRegistration.businessKey })
      .then(res => {
        if (appRegistration.registerUrl) {
          const registerUri = URI(appRegistration.registerUrl);
          registerUri.addQuery({ requestId: res.requestId });
          // Use href() for explicit string extraction to satisfy lint rule
          const stepupUrl: string = registerUri.href();
          globalThis.location.assign(stepupUrl);
        }
        setRegistration(prev => ({ ...prev, isSaving: false }));
      })
      .catch(() => {
        toast('Error updating registration status', { type: 'error' });
        setRegistration(prev => ({ ...prev, isSaving: false }));
      });
  };

  const renderSelfRegistration = () => {
    return (
      <>
        <TextWrapper>
          You are not authorized to access applications on this site. Please
          click on "Self Registration" to request access.
        </TextWrapper>
        <Button
          data-test-id="self-registration-button"
          loading={registration.isSaving}
          onClick={forwardToSelfRegistration}
        >
          {' '}
          Self Registration
        </Button>
      </>
    );
  };
  const renderNoSelfRegistration = () => {
    const { siteDomain } = props;
    return (
      <StyledDialog data-test-id="self-registration-popup">
        You are not authorized to access applications on this site. Please
        contact your administrator to get access.
        <AccessRequestDialogContainer
          dialogTitle={UserAccessRequest.NEW_ACCESS_POPUP_TITLE}
          accessReason={UserAccessRequest.DEFAULT_NEW_ACCESS_REASON}
          orgUrl={siteDomain}
          templateName={UserAccessRequest.NEW_ACCESS_TEMPLATE_NAME}
          shouldShowInstructionsPopUp={false}
        />
      </StyledDialog>
    );
  };

  const getSelfRegistrationUrl = () => {
    const { tenancy, siteDomain } = props;
    if (tenancy === UserAccessRequest.SMAP_DOMAIN) {
      return env.smapSelfRegistrationUrl;
    } else if (tenancy === UserAccessRequest.MBM_DOMAIN) {
      return env.mbmSelfRegistrationUrl;
    } else if (tenancy === UserAccessRequest.LD_DOMAIN) {
      return builderenv.requestAccessUrl;
    } else {
      return siteDomain;
    }
  };

  const forwardToSelfRegistration = () => {
    const selfRegistrationUrl = sanitizeUrl(getSelfRegistrationUrl());
    globalThis.location.assign(selfRegistrationUrl);
  };

  const isSelfRegistration = () => {
    const { tenancy } = props;
    if (SELF_REGISTRATION_SUPPORTED_TENANCIES.includes(tenancy)) {
      return true;
    } else {
      return false;
    }
  };

  const renderResumeRegistration = () => {
    const {
      appRegistration: { resumeRegisterMessage, resumeRegisterUrl },
    } = props;
    return (
      <>
        <TextWrapper>
          {resumeRegisterMessage ||
            'Click "Resume Registration" to continue or check status of your self registration'}
        </TextWrapper>
        <Button
          onClick={() => {
            if (resumeRegisterUrl) {
              globalThis.location.assign(sanitizeUrl(`${resumeRegisterUrl}`));
            }
          }}
        >
          {' '}
          Resume Registration
        </Button>
      </>
    );
  };

  const renderRegistration = () => {
    const {
      appRegistration: { registerMessage },
    } = props;
    return (
      <>
        <TextWrapper>
          {registerMessage ||
            'You have not being provisioned to an application. Click "Register Now" to initiate self registration'}
        </TextWrapper>
        <Button loading={registration.isSaving} onClick={initiateRegistration}>
          {' '}
          Register Now
        </Button>
      </>
    );
  };

  const registrationError = () => {
    return (
      <Message>There is an error retrieving the registration status.</Message>
    );
  };

  const renderMyappsError = () => {
    const { myapps, myappsErrors } = props;
    return (
      <Message
        actions={[
          <Button
            key="retry-btn"
            onClick={handleRetry}
            data-test-id="myapps-retry"
          >
            Retry
          </Button>,
        ]}
        error
        data-test-id="partial-retrieval-error"
      >
        {getErrorMessage(myappsErrors, myapps)}
      </Message>
    );
  };
  const renderDeactivateUserMessage = (
    orgName: string,
    adminEmail: string,
    shouldShowInstructionsPopUp: boolean
  ) => {
    const { siteDomain, noAppsMessage } = props;
    return (
      <StyledDialog data-test-id="deactivate-no-apps-message">
        {noAppsMessage || (
          <>
            Your account has been disabled due to inactivity. Click
            <CustomLink underline onClick={handleRequestAccessClick}>
              here
            </CustomLink>
            to submit a request to reactivate your account.
          </>
        )}
        <AccessRequestDialogContainer
          dialogTitle={UserAccessRequest.REACTIVATE_POPUP_TITLE}
          accessReason={UserAccessRequest.DEFAULT_REACTIVATION_REASON}
          orgName={orgName}
          orgUrl={siteDomain}
          templateName={UserAccessRequest.REACTIVATE_ACCESS_TEMPLATE_NAME}
          adminEmail={adminEmail}
          shouldShowInstructionsPopUp={shouldShowInstructionsPopUp}
        />
      </StyledDialog>
    );
  };

  const renderGenericNoAppMessage = () => {
    const { noAppsMessage } = props;
    return (
      <StyledDialog data-test-id="generic-no-apps-message">
        {noAppsMessage || (
          <>
            You have not been provisioned any applications or your account might
            be inactive. Please contact your administrator or support team.
          </>
        )}
      </StyledDialog>
    );
  };

  const renderNoAppMessageForEnvironmentChange = () => {
    return (
      <StyledDialog data-test-id="generic-no-apps-message-environment">
        {<>No applications available for selected environment.</>}
      </StyledDialog>
    );
  };

  const getFilteredAccount = () => {
    const { activeOrgId, userAccounts } = props;
    return userAccounts.length > 1
      ? userAccounts?.find(a => a.orgClaim && a.orgClaim.orgId === activeOrgId)
      : userAccounts[0];
  };

  const getOrgName = (filteredAccount: UserAccount) => {
    const { tenancy } = props;
    return filteredAccount.orgClaim &&
      !SELF_REGISTRATION_SUPPORTED_TENANCIES.includes(tenancy)
      ? filteredAccount.orgClaim.name
      : '';
  };

  const renderUnAuthorizedNoAppMessage = () => {
    return (
      <ButtonWrapper data-test-id="self-registration">
        {isSelfRegistration()
          ? renderSelfRegistration()
          : renderNoSelfRegistration()}
      </ButtonWrapper>
    );
  };
  const renderAuthorizedNoAppMessage = (userAccounts: UserAccount[]) => {
    const filteredAccount = getFilteredAccount();
    const orgName = getOrgName(filteredAccount);
    const filteredAccountWhereSecureFlagIsTrue = userAccounts.find(
      a => a.secureFlag === true
    );
    const adminEmail = filteredAccount.adminEmail;
    if (
      filteredAccount.isInternalUser === false &&
      filteredAccount.accountStatusCode === 'D'
    ) {
      return renderDeactivateUserMessage(orgName, adminEmail, false);
    } else if (
      (filteredAccount.accountStatusCode === 'D' ||
        filteredAccount.accountStatusCode === 'R') &&
      filteredAccountWhereSecureFlagIsTrue?.accountStatusCode === 'A' &&
      filteredAccountWhereSecureFlagIsTrue?.hasSecureNontaRole === true
    ) {
      return renderDeactivateUserMessage(orgName, adminEmail, false);
    } else if (
      (filteredAccount.accountStatusCode === 'D' ||
        filteredAccount.accountStatusCode === 'R') &&
      (filteredAccountWhereSecureFlagIsTrue?.hasSecureNontaRole === false ||
        filteredAccountWhereSecureFlagIsTrue?.accountStatusCode === 'R')
    ) {
      return renderDeactivateUserMessage(orgName, adminEmail, true);
    } else {
      return renderGenericNoAppMessage();
    }
  };

  const checkForSpecificNoAppScenario = (userAccounts: UserAccount[]) => {
    return !userAccounts || userAccounts.length === 0
      ? renderUnAuthorizedNoAppMessage()
      : renderAuthorizedNoAppMessage(userAccounts);
  };

  const renderNoApps = () => {
    const { enableRegister, userAccounts, siteProvisioningStores } = props;
    let content: React.ReactNode;
    if (enableRegister) {
      let inner: React.ReactNode;
      if (registration.error) {
        inner = registrationError();
      } else if (registration.resume) {
        inner = renderResumeRegistration();
      } else {
        inner = renderRegistration();
      }
      content = <ButtonWrapper>{inner}</ButtonWrapper>;
    } else if (
      env.accessMessageEnabled &&
      (siteProvisioningStores || []).includes(2)
    ) {
      content = checkForSpecificNoAppScenario(userAccounts);
    } else {
      content = renderGenericNoAppMessage();
    }
    return <Message data-test-id="no-apps-message">{content}</Message>;
  };

  const renderApps = () => {
    const { myapps, activeOrgId, siteDomain, user } = props;

    const richAppTiles = getTiles(
      myapps,
      'embedded',
      siteDomain,
      enableAppEnvironmentResolved,
      appEnv
    );

    const standardAppTiles = getTiles(
      myapps,
      'standard',
      siteDomain,
      enableAppEnvironmentResolved,
      appEnv,
      activeOrgId
    );

    return (
      <Apps ref={appsRef} data-test-id="app-lists">
        {standardAppTiles.length === 0 && richAppTiles.length === 0 ? (
          renderNoAppMessageForEnvironmentChange()
        ) : (
          <>
            <RichAppList tiles={richAppTiles} />
            <StandardAppListDummy
              tiles={standardAppTiles}
              handleClick={handleClick}
              impersonationActive={!!user.profile.superuser}
              borderColor={lineColor}
              cardStyle={cardStyle}
              cardTheme={cardTheme}
              currentPage={props.currentPage}
            />
          </>
        )}
      </Apps>
    );
  };

  const renderContent = () => {
    const {
      myappsErrors,
      myapps,
      myappsMeta: { pending: fetchAppsPending, successful, error },
    } = props;
    if (fetchAppsPending || registration.isFetching) {
      return <Loading message="Loading apps" />;
    } else if (successful) {
      // Show if any upstream errors are reported.
      const errors = myappsErrors.length > 0 && renderMyappsError();

      // Show if no apps AND no upstream errors
      const noApps =
        myapps.length === 0 && myappsErrors.length === 0 && renderNoApps();

      // Show if any apps
      const appsList = myapps.length > 0 && renderApps();
      if (errors) {
        // Removed legacy tracking code; replaced by unified tracking hook earlier.
      }
      return (
        <>
          {errors}
          {noApps}
          {appsList}
        </>
      );
    } else if (error) {
      return (
        <Message
          error
          actions={[
            <Button
              key="apps-retry-btn"
              onClick={handleRetry}
              data-test-id="myapps-retry"
            >
              Retry
            </Button>,
          ]}
        >
          {typeof error === 'string' && error.length > 0
            ? error
            : 'Error loading apps'}
        </Message>
      );
    }

    return null;
  };

  const renderOrgSelector = () => {
    const { activeOrgId, orgs } = props;

    if (orgs.length > 0) {
      const active = orgs.find(org => org.orgId === activeOrgId);

      return (
        <SelectWrapper>
          <SelectLabel
            data-test-id="org-caption"
            variant="caption"
            gutterBottom
            cardTheme={cardTheme}
          >
            Organization
          </SelectLabel>
          <FlexSelect
            width={setOrgSelectWidth()}
            onChange={handleOrgChange}
            value={active.orgId}
            disabled={orgs.length === 0}
            data-test-id="org-select"
            style={{
              color:
                cardTheme === cardThemeOptions.Light
                  ? `${defaultTheme.colors.aux.black}`
                  : '#ffffff',
              border:
                cardTheme === cardThemeOptions.Light
                  ? 'none'
                  : '1px solid #ffffff',
            }}
            cardTheme={cardTheme}
          >
            {orgs.map(org => (
              <MenuItem
                key={org.orgId}
                id={org.orgId}
                value={org.orgId}
                style={{
                  fontFamily: 'OptumSans',
                  cursor: 'default',
                }}
              >
                {org.name}
              </MenuItem>
            ))}
          </FlexSelect>
        </SelectWrapper>
      );
    }
  };

  const renderEnvironmentSelector = () => {
    const enableAppEnvironment = enableAppEnvironmentResolved;
    const appsEnvironmentList = appsEnvironmentListResolved;
    // for existing sites appsEnvironmentList will be undefined which will break to render the appTiles if this doesnt checked
    const environmentList = appsEnvironmentList
      ? appsEnvironmentList
          .split(',')
          .map(value => value.trim())
          .filter(value => value !== '')
          .map(str => str.charAt(0).toUpperCase() + str.slice(1))
      : [];

    if (enableAppEnvironment && environmentList.length > 0) {
      return (
        <SelectWrapper>
          <SelectLabel
            data-test-id="env-caption"
            variant="caption"
            gutterBottom
            cardTheme={cardTheme}
          >
            Environment
          </SelectLabel>
          <FlexSelect
            width={setOrgSelectWidth()}
            onChange={handleEnvironmentChange}
            disabled={environmentList.length === 0}
            data-test-id="env-select"
            value={appEnv}
            onOpen={() => setIsEnvOpen(true)}
            onClose={() => setIsEnvOpen(false)}
            // Inline style derived from current cardTheme; avoids invalid function interpolation
            style={{
              color:
                !cardTheme || cardTheme === cardThemeOptions.Light
                  ? `${defaultTheme.colors.aux.black}`
                  : '#ffffff',
            }}
            cardTheme={cardTheme}
          >
            {environmentList.map(it => (
              <MenuItem key={it} value={it} style={{ fontFamily: 'OptumSans' }}>
                {it}
              </MenuItem>
            ))}
          </FlexSelect>
        </SelectWrapper>
      );
    }
  };

  if (visible === false || isCustomWidget) {
    return <></>;
  }

  // Define other functions and JSX components as required
  return (
    <Wrapper
      data-test-id="myapps-widget"
      style={{
        marginTop: '0',
        // Bottom padding (measured) adds a tiny space below content
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        paddingTop: `${paddingData?.top}px`,
        paddingRight: `${paddingData?.right}px`,
        paddingBottom: `${(paddingData?.bottom ?? 0) + EXTRA_BOTTOM_GAP_PX}px`,
        paddingLeft: `${paddingData?.left}px`,
        borderWidth: `${borderData?.thickness}px`,
        borderTop: `${
          borderData?.isTopBorderVisible ?? true
            ? `${borderData?.thickness}px solid`
            : 'none'
        }`,
        borderBottom: `${
          borderData?.isBottomBorderVisible ?? true
            ? `${borderData?.thickness}px solid`
            : 'none'
        }`,
        borderLeft: `${
          borderData?.isLeftBorderVisible ?? true
            ? `${borderData?.thickness}px solid`
            : 'none'
        }`,
        borderRight: `${
          borderData?.isRightBorderVisible ?? true
            ? `${borderData?.thickness}px solid`
            : 'none'
        }`,
        borderColor: `${lineColor}`,
        borderRadius: `${borderData?.radius}px`,
        height: '100%',
        width: '100%',
        fontFamily: font,
      }}
    >
      <Header ref={headerRef}>
        {renderOrgSelector()}
        {renderEnvironmentSelector()}
      </Header>
      <Apps>{renderContent()}</Apps>
      {/* Expose optional onClose for future dialog integration (noop usage to appease lint) */}
      {onCloseCallback && (
        <span style={{ display: 'none' }} data-test-id="myapps-onclose-hook" />
      )}
    </Wrapper>
  );
};

export default MyApps;
